import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Download, ExternalLink, FileText, Loader2, RotateCcw, Trophy, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { competitionsService } from '../services/competitions';
import {
  INSTRUCTIONS_AUDIENCES,
  buildInstructionsData,
  instructionsFileName,
  instructionsLanguage,
  type InstructionsAudience,
  type InstructionsLanguage,
} from '../components/competition-instructions/instructions-data';
import { canDownloadInstructions } from '../components/competition-instructions/instructions-access';
import type { Competition } from '../types/competition';
import { cn } from '../lib/utils';

type SheetsState =
  | { status: 'loading' }
  | { status: 'ready'; urls: Record<InstructionsAudience, string> }
  | { status: 'error' }
  | { status: 'not_found' };

const LANGUAGES: InstructionsLanguage[] = ['it', 'en'];

const AUDIENCE_ICONS = { athletes: Trophy, teamManagers: Users } as const;

/**
 * `/competitions/:slug/instructions.pdf` — the entry instructions of one
 * competition, as two one-page PDFs: one for the athletes, one for the squads'
 * team managers. For the people who hand them out — the competition's admins
 * and its desk (see canDownloadInstructions) — behind sign-in.
 *
 * The files are made here, in the browser: the page reads the competition,
 * loads react-pdf on its own (a large library nobody else needs) and offers
 * each file to download or open. `?lang=it|en` picks the sheets' language,
 * which otherwise follows the app's.
 */
export default function CompetitionInstructionsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [sheets, setSheets] = useState<SheetsState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const allowed = !!slug && canDownloadInstructions(user, slug);
  const language = instructionsLanguage(searchParams.get('lang'), i18n.language);

  // The sheets speak their own language whatever the app is set to; the page
  // around them stays in the app's.
  const sheetT: TFunction = typeof i18n.getFixedT === 'function' ? i18n.getFixedT(language) : t;

  useEffect(() => {
    if (!slug || !allowed) return;
    let active = true;
    competitionsService
      .getCompetitionBySlug(slug)
      .then((found) => active && setCompetition(found))
      .catch(() => active && setSheets({ status: 'not_found' }));
    return () => {
      active = false;
    };
  }, [slug, allowed]);

  // Made again when the language changes or a retry is asked for. The previous
  // files' object URLs are released when they are replaced.
  useEffect(() => {
    if (!competition) return;
    let active = true;
    const made: string[] = [];
    setSheets({ status: 'loading' });

    (async () => {
      try {
        const { renderInstructionsPdf } = await import(
          '../components/competition-instructions/render-instructions-pdf'
        );
        const data = buildInstructionsData(competition, window.location.origin, language);
        const blobs = await Promise.all(
          INSTRUCTIONS_AUDIENCES.map((audience) => renderInstructionsPdf(data, sheetT, audience))
        );
        if (!active) return;
        const [athletes, teamManagers] = blobs.map((blob) => URL.createObjectURL(blob));
        made.push(athletes, teamManagers);
        setSheets({ status: 'ready', urls: { athletes, teamManagers } });
      } catch (error) {
        console.error('Failed to build the instructions PDFs:', error);
        if (active) setSheets({ status: 'error' });
      }
    })();

    return () => {
      active = false;
      made.forEach((url) => URL.revokeObjectURL(url));
    };
    // `sheetT` is a new function on every render; the language is what it
    // depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition, language, attempt]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);

  // Two segments, not the circuit's three: on a phone, with the navbar's own
  // buttons beside it, a third pushed the navbar off the screen. The way up to
  // the circuit is one tap further, on the competition's page.
  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: competition
        ? [
            { label: competition.name, onClick: () => navigate(`/competitions/${competition.slug}`) },
            { label: t('competitionInstructions.page.breadcrumb') },
          ]
        : [],
      loading: allowed && !competition && sheets.status === 'loading',
    }),
    [competition?.name, competition?.slug, sheets.status, allowed]
  );

  if (!slug) return <Navigate to="/not-found" replace />;

  if (!allowed || sheets.status === 'not_found') {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <CardContent
            className="pt-6 text-center"
            data-testid={allowed ? 'instructions-not-found' : 'instructions-forbidden'}
          >
            <p className="text-sm text-muted-foreground">
              {allowed ? t('competitionInstructions.page.notFound') : t('competitionInstructions.page.forbidden')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="competition-instructions">
      <Card>
        <CardContent className="space-y-4 p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <FileText className="mt-0.5 size-6 shrink-0 text-primary" aria-hidden />
            <div className="min-w-0">
              <h1 className="text-lg font-semibold">{t('competitionInstructions.page.title')}</h1>
              {competition && (
                <p className="truncate text-sm text-muted-foreground">
                  {[competition.trophy?.name, competition.name].filter(Boolean).join(' — ')}
                </p>
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">{t('competitionInstructions.page.description')}</p>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('competitionInstructions.page.language')}</span>
            {LANGUAGES.map((code) => (
              <Link
                key={code}
                to={{ search: `?lang=${code}` }}
                replace
                className={cn(
                  'rounded-md border px-2.5 py-1 text-xs font-medium uppercase',
                  code === language ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
                )}
                aria-current={code === language ? 'true' : undefined}
                data-testid={`instructions-lang-${code}`}
              >
                {code}
              </Link>
            ))}
          </div>

          {sheets.status === 'loading' && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="instructions-preparing">
              <Loader2 className="size-4 animate-spin" />
              {t('competitionInstructions.page.preparing')}
            </p>
          )}

          {sheets.status === 'error' && (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-destructive" data-testid="instructions-error">
                {t('competitionInstructions.page.error')}
              </p>
              <Button variant="outline" size="sm" className="gap-2" onClick={retry}>
                <RotateCcw className="size-4" />
                {t('competitionInstructions.page.retry')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {sheets.status === 'ready' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {INSTRUCTIONS_AUDIENCES.map((audience) => {
            const Icon = AUDIENCE_ICONS[audience];
            const url = sheets.urls[audience];
            return (
              <Card key={audience} data-testid={`instructions-${audience}`}>
                <CardContent className="flex h-full flex-col gap-3 p-4 sm:p-5">
                  <div className="flex items-center gap-2">
                    <Icon className="size-5 text-primary" aria-hidden />
                    <h2 className="font-semibold">{t(`competitionInstructions.page.${audience}Title`)}</h2>
                  </div>
                  <p className="flex-1 text-sm text-muted-foreground">
                    {t(`competitionInstructions.page.${audience}Description`)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild className="gap-2">
                      <a
                        href={url}
                        download={instructionsFileName(slug, language, audience)}
                        data-testid={`instructions-download-${audience}`}
                      >
                        <Download className="size-4" />
                        {t('competitionInstructions.page.download')}
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="gap-2">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`instructions-open-${audience}`}
                      >
                        <ExternalLink className="size-4" />
                        {t('competitionInstructions.page.open')}
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
